/**
 * @engine/vfx — VFX shader cache (id → compiled shader source).
 *
 * Compiled VFX shaders no longer live inside the `.vfx` document or the compiled
 * bundle. The importer emits them into this content-addressed cache (id = hash of
 * the shader source, so identical shaders across effects dedupe) and the bundle only
 * references shader **ids**. At runtime the cache is populated from the on-disk shader
 * files (editor) or a build-emitted `vfx-shaders.json` snapshot (player), then systems
 * resolve their stage shaders by id.
 *
 * Mirrors `@engine/render2d`'s engine `ShaderCache` (name→WGSL) but keyed by a stable,
 * content-addressed, format-tagged id — ready for a future Slang codegen backend.
 */

/** Shader source language a cached VFX shader is emitted in. */
export type VFXShaderFormat = 'wgsl' | 'slang';

/** VFS directory holding content-addressed VFX shader files (`<id>.wgsl`). */
export const VFX_SHADER_CACHE_DIR = '/.cache/vfx-shaders';

const _cache = new Map<string, string>();

export function registerVFXShader(id: string, source: string): void {
  _cache.set(id, source);
}

export function registerVFXShaders(map: Record<string, string>): void {
  for (const id in map) _cache.set(id, map[id]);
}

export function hasVFXShader(id: string): boolean {
  return _cache.has(id);
}

/** Get a cached shader source by id. Throws if absent (misconfigured build/import). */
export function getVFXShader(id: string): string {
  const s = _cache.get(id);
  if (s === undefined) throw new Error(`[VFXShaderCache] shader not registered: ${id}`);
  return s;
}

export function getVFXShaderOrNull(id: string): string | undefined {
  return _cache.get(id);
}

/** Snapshot the whole cache (used by the build to emit `vfx-shaders.json`). */
export function getAllVFXShaders(): Record<string, string> {
  return Object.fromEntries(_cache);
}

export function clearVFXShaders(): void {
  _cache.clear();
}

/** VFS path a shader id resolves to in the editor's on-disk cache. */
export function vfxShaderPath(id: string): string {
  return `${VFX_SHADER_CACHE_DIR}/${id}.wgsl`;
}

/**
 * Content-addressed, format-tagged id for a compiled shader. Identical sources
 * (across systems/effects) produce the same id, so the cache dedupes them.
 * Uses cyrb53 (fast, 64-bit) — no crypto dependency, deterministic across platforms.
 */
export function vfxShaderId(source: string, format: VFXShaderFormat = 'wgsl'): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < source.length; i++) {
    const ch = source.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hex = (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
  return `${format}_${hex}`;
}
